/**
 * Dashboard Layout Component
 *
 * Main layout wrapper for authenticated dashboard pages.
 * Features:
 * - Collapsible sidebar with logo, nav, and utility controls (left)
 * - Content area (right) with rounded top-left corner
 */

import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Avatar, AvatarImage, AvatarFallback, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@provenleap/ui";
import { MagnifyingGlass, ChartPieSlice, Files, Users, Target, BellRinging, Kanban, GearSix, User, Key, CreditCard, Tag, Palette, SignOut } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { Sidebar } from "./Sidebar";
import { NotificationBell } from "./NotificationBell";
import { DeletionBanner } from "../account/DeletionBanner";
import { IntegrationAlertBanner } from "./IntegrationAlertBanner";
import { BreadcrumbHost } from "../reports/ReportBreadcrumb";
import { BreadcrumbProvider } from "../../lib/breadcrumb";
import { apiGet } from "../../lib/apiClient";
import { logout } from "../../lib/auth";

interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  displayName?: string | null;
  avatarUrl?: string | null;
}

interface DashboardLayoutProps {
  /** The current authenticated user */
  user: User;
  /** API base URL for verification requests */
  apiBaseUrl?: string;
  /** Child components to render in the main content area */
  children: React.ReactNode;
}

function getInitials(displayName: string | null | undefined, email: string): string {
  if (displayName) {
    const parts = displayName.split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  }
  return email.substring(0, 2).toUpperCase();
}

const SEARCH_PAGES: { label: string; to: string; icon: Icon }[] = [
  { label: "Dashboard", to: "/dashboard", icon: ChartPieSlice },
  { label: "Reports", to: "/reports", icon: Files },
  { label: "Clients", to: "/clients", icon: Users },
  { label: "Goals", to: "/goals", icon: Target },
  { label: "Alerts", to: "/alerts", icon: BellRinging },
  { label: "Tasks", to: "/tasks", icon: Kanban },
  { label: "Settings", to: "/settings", icon: GearSix },
  { label: "Profile", to: "/settings/profile", icon: User },
  { label: "Security", to: "/settings/security", icon: Key },
  { label: "Billing", to: "/settings/billing", icon: CreditCard },
  { label: "Team", to: "/settings/team", icon: Users },
  { label: "Tags", to: "/settings/tags", icon: Tag },
  { label: "Themes", to: "/settings/themes", icon: Palette },
];

/** Expanded search notch width. */
const SEARCH_NOTCH_WIDTH = 229;
/** Number of breadcrumb items that triggers search collapse */
const BREADCRUMB_COLLAPSE_THRESHOLD = 3;

/** Hook to detect when breadcrumb has enough items to collapse search */
function useSearchCollapsed() {
  const [collapsed, setCollapsed] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const observer = new ResizeObserver(() => {
      setContainerWidth(containerEl.offsetWidth);
    });
    observer.observe(containerEl);
    return () => observer.disconnect();
  }, []);

  // Watch breadcrumb child count via MutationObserver
  useEffect(() => {
    const breadcrumbEl = breadcrumbRef.current;
    if (!breadcrumbEl) return;

    const portalTarget = breadcrumbEl.querySelector("#header-breadcrumb") ?? breadcrumbEl;

    const checkChildren = () => {
      // Count direct children inside the breadcrumb nav's flex container
      const nav = portalTarget.querySelector("nav > div") ?? portalTarget;
      const childCount = nav.children.length;
      setCollapsed(childCount >= BREADCRUMB_COLLAPSE_THRESHOLD);
    };

    checkChildren();

    const observer = new MutationObserver(checkChildren);
    observer.observe(portalTarget, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return { collapsed, containerWidth, breadcrumbRef, containerRef };
}

function useCommandPalette() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = query.trim()
    ? SEARCH_PAGES.filter((p) =>
        p.label.toLowerCase().includes(query.toLowerCase())
      )
    : SEARCH_PAGES;

  useEffect(() => {
    setSelectedIndex(-1);
  }, [query]);

  // Cmd+K / Ctrl+K shortcut — skip when the report editor overlay is open, since
  // it owns ⌘K for its own widget command palette.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        if (document.querySelector('[data-testid="report-editor-layout"]')) return;
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
      setSelectedIndex(-1);
    }
  }, [open]);

  const handleSelect = useCallback((to: string) => {
    navigate({ to });
    setOpen(false);
  }, [navigate]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => (i - 1 + results.length) % results.length);
    } else if ((e.key === "Enter" || e.key === "Tab") && results.length > 0) {
      e.preventDefault();
      const target = selectedIndex >= 0 ? results[selectedIndex] : results[0];
      if (target) handleSelect(target.to);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return { open, setOpen, query, setQuery, selectedIndex, results, inputRef, handleSelect, handleKeyDown };
}

/** Full search trigger with text + shortcut hint */
function SearchTriggerFull() {
  return (
    <div className="flex items-center gap-2 w-full text-[13.5px] text-foreground hover:text-foreground/70 transition-colors cursor-pointer">
      <MagnifyingGlass size={16} className="shrink-0" />
      <span className="flex-1 text-left">Search</span>
      <kbd className="text-xs font-medium border border-input rounded-[9px] px-1.5 py-0.5 bg-background ml-2">
        <span className="text-sm">⌘</span> + K
      </kbd>
    </div>
  );
}


/** The command palette modal (portaled to body) */
function CommandPaletteModal({ open, setOpen, query, setQuery, selectedIndex, results, inputRef, handleSelect, handleKeyDown }: ReturnType<typeof useCommandPalette>) {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setOpen(false)}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/35"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          />

          {/* Palette */}
          <motion.div
            className="relative w-full max-w-sm bg-background border border-border rounded-3xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 pt-6 pb-4" style={{ margin: "0 6px" }}>
              <div className="flex-1 flex items-center gap-2 rounded-lg border-[0.5px] border-input px-3 py-2">
                <MagnifyingGlass size={16} className="text-foreground/80 shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search pages..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-sm text-foreground bg-transparent placeholder:text-foreground/80"
                  style={{ outline: "none", boxShadow: "none", border: "none" }}
                />
              </div>
              <kbd
                className="text-xs text-foreground font-medium border border-input rounded-md px-1.5 py-0.5 bg-background cursor-pointer"
                onClick={() => setOpen(false)}
              >
                esc
              </kbd>
            </div>

            {/* Results */}
            <div className="h-[600px] overflow-y-auto py-1">
              {results.length > 0 ? (
                results.map((page, i) => (
                  <div key={page.to} className="px-1.5">
                    <motion.button
                      type="button"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.03 * (i + 1) }}
                      onClick={() => handleSelect(page.to)}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(-1)}
                      className={`w-full text-left px-3 py-4 text-sm flex items-center gap-3 cursor-pointer rounded-[14px] border ${
                        i === selectedIndex
                          ? "bg-muted/20 text-foreground border-input shadow-xs"
                          : "text-foreground border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs"
                      }`}
                    >
                      <page.icon size={18.7} className="text-foreground shrink-0" />
                      <span>{page.label}</span>
                      <span className="ml-auto text-[14px] text-foreground">{page.to}</span>
                    </motion.button>
                    {i < results.length - 1 && (
                      <div
                        className="mx-4"
                        style={{
                          borderBottom: "0.5px solid hsl(var(--input) / 0.4)",
                          opacity: hoveredIndex === i || hoveredIndex === i + 1 ? 0 : 1,
                        }}
                      />
                    )}
                  </div>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No results found.
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

interface AccountStatusResponse {
  data: {
    status: string;
    deletion_date?: string;
  };
}

export function DashboardLayout({
  user,
  children,
}: DashboardLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { collapsed: searchCollapsed, containerWidth, breadcrumbRef, containerRef } = useSearchCollapsed();
  const cmdPalette = useCommandPalette();
  const navigate = useNavigate();

  // Delay removing profile notch radius until search has arrived
  const [profileRounded, setProfileRounded] = useState(!searchCollapsed);
  useEffect(() => {
    if (!searchCollapsed) {
      // Search leaving — restore radius immediately
      setProfileRounded(true);
      return;
    }
    // Search arriving — wait for slide animation to finish
    const timer = setTimeout(() => setProfileRounded(false), 350);
    return () => clearTimeout(timer);
  }, [searchCollapsed]);

  // Query account status for deletion banner (AC#6, #16)
  const accountStatusQuery = useQuery({
    queryKey: ["account-status"],
    queryFn: async () => {
      const response = await apiGet("/api/account/status");
      if (!response.ok) return null;
      const data = (await response.json()) as AccountStatusResponse;
      return data.data;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const isPendingDeletion =
    accountStatusQuery.data?.status === "pending_deletion";
  const rawDeletionDate = accountStatusQuery.data?.deletion_date;
  const deletionDate = rawDeletionDate
    ? new Date(rawDeletionDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : undefined;

  return (
    <BreadcrumbProvider>
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* Deletion warning banner (AC#16 — not a modal, user can still use app) */}
      {isPendingDeletion && deletionDate && (
        <DeletionBanner deletionDate={deletionDate} />
      )}

      {/* Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((c) => !c)}
        />

        {/* Main content column */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar: thin strip with notches */}
          <div ref={containerRef} className="relative shrink-0 bg-background h-[21px] overflow-visible">
            {/* Breadcrumb notch — single container so border layer matches content width */}
            <div className="absolute z-20 left-10 top-0 h-[48px]">
              <div ref={breadcrumbRef} className="relative h-full flex items-center px-5 bg-background rounded-b-[20px]">
                <div id="header-breadcrumb" className="flex items-center min-w-0">
                  <BreadcrumbHost />
                </div>
                {/* Border+shadow overlay (clipped at header line) */}
                <div className="absolute inset-0 rounded-b-[20px] pointer-events-none" style={{ clipPath: "inset(21px -10px -10px -10px)", border: "0.5px solid var(--hairline)", borderTop: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }} />
              </div>
            </div>

            {/* Search notch — slides from center into profile notch */}
            {containerWidth > 0 && (
              <>
                {/* Search content layer */}
                <motion.div
                  className="absolute z-20 top-0 h-[48px]"
                  initial={false}
                  animate={{
                    right: searchCollapsed ? 126 : (containerWidth / 2) - (SEARCH_NOTCH_WIDTH / 2),
                    width: searchCollapsed ? 48 : SEARCH_NOTCH_WIDTH,
                  }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                >
                  <button
                    type="button"
                    onClick={() => cmdPalette.setOpen(true)}
                    className="relative h-full w-full flex items-center justify-center bg-background cursor-pointer"
                    style={{ borderRadius: searchCollapsed ? "0 0 0 20px" : "0 0 20px 20px" }}
                    title="Search (⌘K)"
                  >
                    {/* Full search trigger — visible when expanded */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center px-4"
                      animate={{ opacity: searchCollapsed ? 0 : 1 }}
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                      <div className="w-[213px]">
                        <SearchTriggerFull />
                      </div>
                    </motion.div>
                    {/* Icon only — visible when collapsed */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{ opacity: searchCollapsed ? 1 : 0 }}
                      transition={{ duration: 0.2, ease: "easeInOut", delay: searchCollapsed ? 0.15 : 0 }}
                    >
                      <MagnifyingGlass size={16} className="text-foreground" />
                    </motion.div>
                  </button>
                </motion.div>
                {/* Search border layer — travels with search, fades when docking */}
                <motion.div
                  className="absolute z-30 top-0 h-[48px] pointer-events-none"
                  initial={false}
                  animate={{
                    right: searchCollapsed ? 126 : (containerWidth / 2) - (SEARCH_NOTCH_WIDTH / 2),
                    width: searchCollapsed ? 48 : SEARCH_NOTCH_WIDTH,
                    opacity: searchCollapsed ? 0 : 1,
                    borderRadius: searchCollapsed ? "0 0 0 20px" : "0 0 20px 20px",
                  }}
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  style={{ clipPath: "inset(21px -10px -10px -10px)", border: "0.5px solid var(--hairline)", borderTop: "none", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}
                />
              </>
            )}

            {/* Profile notch — content (unclipped, above header) */}
            <div className="absolute z-20 right-6 top-0 h-[48px]">
              <div className={`h-full flex items-center gap-0.5 px-3 bg-background rounded-br-[20px] ${profileRounded ? "rounded-bl-[20px]" : ""}`}>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center rounded-lg p-2 text-foreground hover:bg-muted/30 transition-colors cursor-pointer"
                    title={user.displayName ?? user.email}
                  >
                    <Avatar className="h-7 w-7 shrink-0 border-[0.5px] border-foreground">
                      {user.avatarUrl ? (
                        <AvatarImage src={user.avatarUrl} alt="Profile avatar" />
                      ) : null}
                      <AvatarFallback className="bg-transparent text-foreground text-[10px] font-medium">
                        {getInitials(user.displayName, user.email)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="filter-popup-motion rounded-xl p-2 shadow-md"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  <DropdownMenuItem className="min-h-11 text-[13px] cursor-pointer rounded-lg" asChild>
                    <Link to="/settings/profile">
                      <User size={14} className="mr-1" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg"
                    onClick={() => logout((to) => navigate({ to }))}
                  >
                    <SignOut size={14} className="mr-1" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            </div>
            {/* Profile notch — border+shadow (clipped at header line, on top) */}
            <div className="absolute z-30 right-6 top-0 h-[48px] pointer-events-none">
              <motion.div
                className="h-full rounded-b-[20px] ml-auto"
                animate={{ width: searchCollapsed ? 150 : 114 }}
                transition={{
                  duration: 0.25,
                  ease: [0.4, 0, 0.2, 1],
                  delay: searchCollapsed ? 0.35 : 0,
                }}
                style={{ clipPath: "inset(21px -10px -10px -10px)", borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)", borderRight: "0.5px solid var(--hairline)", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" }}
              />
            </div>
          </div>

          {/* Page Content */}
          <main className="flex-1 overflow-auto rounded-tl-[2rem] pt-12" style={{ backgroundColor: "var(--app-surface)", boxShadow: "inset 1px 1px 3px -1px rgba(0,0,0,0.05)", borderLeft: "0.5px solid var(--hairline)", borderTop: "0.5px solid var(--hairline)" }}>
            <IntegrationAlertBanner />
            {children}
          </main>
        </div>

        {/* Portal target for right sidebar — same level as left Sidebar */}
        <div id="right-sidebar-slot" className="shrink-0" />
      </div>
      <CommandPaletteModal {...cmdPalette} />
    </div>
    </BreadcrumbProvider>
  );
}
